import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

// Below this the viewport shrank for a browser-chrome transition rather than a keyboard.
const KEYBOARD_MIN_HEIGHT = 120;

/**
 * Keeps a bottom-anchored panel inside what the keyboard has left of the screen. Returns the ref to
 * put on it.
 *
 * iOS opens the keyboard without touching the layout viewport — `documentElement.clientHeight`
 * stays at its full height — and shrinks the *visual* viewport instead, scrolling the document down
 * so the focused field stays on screen. The panel's bottom edge rides along with that and needs no
 * help. Its height does: every CSS unit available to it (`dvh`, `svh`, a percentage of the fixed
 * containing block) measures the layout viewport, so a sheet capped at `90dvh` stays 643px tall in
 * a 377px window and wears its own header off the top of the screen.
 *
 * `visualViewport.height` is the only number that knows, so the cap is written straight to the
 * node — the browser fires `resize` for every frame of the keyboard's slide-up, and re-rendering
 * each time reads as the panel hunting for somewhere to land.
 */
export function useKeyboardFit(): RefObject<HTMLDivElement> {
  const paperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }

    const fit = () => {
      const paper = paperRef.current;
      if (!paper) {
        return;
      }
      const isKeyboardOpen = viewport.height < document.documentElement.clientHeight - KEYBOARD_MIN_HEIGHT;
      // Cleared rather than restored, so the panel goes back to the cap its own classes give it.
      paper.style.maxHeight = isKeyboardOpen ? `${viewport.height}px` : '';
    };

    fit();
    viewport.addEventListener('resize', fit);
    return () => viewport.removeEventListener('resize', fit);
  }, []);

  return paperRef;
}
